import React, { FC, useState, useEffect } from 'react';
import { CourseData, PricingOption, TYPE_LABELS } from '../types';
import { calculatePricingDetails, formatPaymentPeriod } from 'entities/course-catalog';
import { IconP1, IconP2, IconP3, IconP4, IconPython, IconJavaScript } from '../icons';
import styles from './CourseCard.module.scss';

interface CourseCardProps {
  course: CourseData;
  pricingAvailable: boolean;
  pricingMessage: string;
  onSelectCourse: (course: CourseData, pricing?: PricingOption) => void;
}

const getIcon = (iconName: string, size: number = 60) => {
  const props = { width: size, height: size };
  switch (iconName) {
    case 'python': return <IconPython {...props} />;
    case 'javascript': return <IconJavaScript {...props} />;
    case 'p1': return <IconP1 {...props} />;
    case 'p2': return <IconP2 {...props} />;
    case 'p3': return <IconP3 {...props} />;
    case 'p4': return <IconP4 {...props} />;
    default: return <IconP1 {...props} />;
  }
};

export const CourseCard: FC<CourseCardProps> = ({ course, pricingAvailable, pricingMessage, onSelectCourse }) => {
  const [selectedFormat, setSelectedFormat] = useState<'online' | 'offline'>('online');
  const [selectedType, setSelectedType] = useState<'individual' | 'closed_group' | 'group'>('group');
  const [showPricing, setShowPricing] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState<PricingOption | null>(null);


  // Автоматически выбираем первую доступную опцию при открытии панели
  useEffect(() => {
    if (showPricing && course.pricing.length > 0) {
      // Ищем первую доступную опцию для текущего формата
      const typesOrder: ('group' | 'closed_group' | 'individual')[] = ['group', 'closed_group', 'individual'];

      for (const type of typesOrder) {
        const pricing = course.pricing.find(p => p.format === selectedFormat && p.type === type);
        if (pricing) {
          setSelectedType(type);
          setSelectedPricing(pricing);
          break;
        }
      }
    }
  }, [showPricing, selectedFormat, course.pricing]);

  const handleCardClick = () => {
    if (!pricingAvailable) return;
    if (course.pricing.length > 0) {
      setShowPricing(!showPricing);
    } else {
      // Для курсов с особым предложением сразу переходим к заявке
      onSelectCourse(course);
    }
  };

  const handlePricingSelect = (pricing: PricingOption) => {
    onSelectCourse(course, pricing);
  };

  const getCurrentPrice = () => {
    if (course.specialOffer) {
      return `${course.specialOffer.monthlyPayment.toLocaleString()} ₽/мес`;
    }
    
    if (course.pricing.length > 0) {
      const currentPricing = course.pricing.find(
        p => p.format === selectedFormat && p.type === selectedType
      );
      if (!currentPricing) return 'Уточнить цену';
      const priceText = course.billingUnit === 'course' ? '₽' : '₽/мес';
      return `${currentPricing.price.toLocaleString()} ${priceText}`;
    }
    
    return 'Уточнить цену';
  };

  const getMinPrice = () => {
    if (course.pricing.length === 0) return null;
    const minPrice = Math.min(...course.pricing.map(p => p.price));
    const priceText = course.billingUnit === 'course' ? '₽' : '₽/мес';
    return `от ${minPrice.toLocaleString()} ${priceText}`;
  };

  return (
    <div 
      className={styles.courseCard}
      style={{ '--card-color': course.color } as React.CSSProperties}
    >
      <div className={styles.cardContent} onClick={handleCardClick}>
        <div className={styles.header}>
          <div className={styles.iconContainer}>
            {getIcon(course.icon, 48)}
          </div>
          <div className={styles.badge}>
            {course.duration}
          </div>
        </div>

        <div className={styles.content}>
          <h3 className={styles.title}>{course.title}</h3>
          
          {course.level && (
            <div className={styles.level}>{course.level}</div>
          )}

          {course.trainingType && (
            <div className={styles.trainingType}>{course.trainingType}</div>
          )}
          
          <div className={styles.frequency}>{course.frequency}</div>
          
          <p className={styles.description}>{course.description}</p>

          {course.trainingType && (
            <div className={styles.stages}>
              <div className={styles.stage}>
                <div className={styles.stageNumber}>1 этап</div>
                <div className={styles.stageTitle}>
                  {course.trainingType === 'СТАНДАРТ' ? 'первые 6 месяцев' : 'первые 3 месяца'}
                </div>
                <div className={styles.stageDescription}>
                  {course.trainingType === 'СТАНДАРТ'
                    ? `углублённое изучение ${course.title.includes('Python') ? 'Python' : 'JavaScript'} и первые проекты`
                    : `интенсивное изучение ${course.title.includes('Python') ? 'Python' : 'JavaScript'} и быстрый старт в разработке`
                  }
                </div>
              </div>
              <div className={styles.stage}>
                <div className={styles.stageNumber}>2 этап</div>
                <div className={styles.stageTitle}>
                  {course.trainingType === 'СТАНДАРТ' ? 'вторые 6 месяцев' : 'вторые 3 месяца'}
                </div>
                <div className={styles.stageDescription}>
                  {course.trainingType === 'СТАНДАРТ'
                    ? 'подготовка к фрилансу и первый реальный опыт разработчика'
                    : 'закрепление навыков и подготовка к самостоятельной работе'
                  }
                </div>
              </div>
            </div>
          )}

          {course.trainingType && (
            <div className={styles.courseInfo}>
              {course.trainingType === 'СТАНДАРТ'
                ? 'Первый курс: 12 месяцев • Второй курс: 6 месяцев'
                : 'Первый курс: 6 месяцев • Второй курс: 3 месяца'
              }
            </div>
          )}

          {course.isBootcamp && (
            <div className={styles.bootcampInfo}>
              {course.timeSlots && (
                <div className={styles.timeSlots}>
                  Время: {course.timeSlots.join(' или ')}
                </div>
              )}
              {course.weekends && (
                <div className={styles.weekends}>
                  Выходные: {course.weekends}
                </div>
              )}
              {course.prerequisites && (
                <div className={styles.prerequisites}>
                  Требования: {course.prerequisites}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.price}>
            {!pricingAvailable ? (
              <div className={styles.mainPrice}>{pricingMessage}</div>
            ) : course.specialOffer ? (
              <div className={styles.specialOffer}>
                <div className={styles.mainPrice}>{getCurrentPrice()}</div>
                <div className={styles.discount}>
                  Скидка при полной оплате: {course.specialOffer.discount.toLocaleString()} ₽
                </div>
              </div>
            ) : showPricing ? (
              <div className={styles.mainPrice}>
                {getCurrentPrice()}
              </div>
            ) : (
              <div className={styles.mainPrice}>
                {getMinPrice() || 'Подробнее'}
              </div>
            )}
          </div>
          
          <div className={styles.arrow}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path 
                d="M7.5 5L12.5 10L7.5 15" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>

      {pricingAvailable && showPricing && course.pricing.length > 0 && (
        <div className={styles.pricingPanel}>
          <div className={styles.pricingHeader}>
            <h4>Выберите формат обучения</h4>
          </div>
          
          <div className={styles.formatSelector}>
            <button
              className={`${styles.formatButton} ${selectedFormat === 'online' ? styles.active : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFormat('online');
              }}
            >
              Онлайн
            </button>
            <button
              className={`${styles.formatButton} ${selectedFormat === 'offline' ? styles.active : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFormat('offline');
              }}
            >
              Оффлайн
            </button>
          </div>


          <div className={styles.typeSelector}>
            {(['group', 'closed_group', 'individual'] as const).map(type => {
              const pricing = course.pricing.find(p => p.format === selectedFormat && p.type === type);
              if (!pricing) return null;

              const priceText = course.billingUnit === 'course' ? '₽' : '₽/мес';

              return (
                <button
                  key={type}
                  className={`${styles.typeButton} ${selectedPricing?.type === type && selectedPricing?.format === selectedFormat ? styles.active : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedType(type);
                    setSelectedPricing(pricing);
                  }}
                >
                  <div className={styles.typeLabel}>{TYPE_LABELS[type]}</div>
                  <div className={styles.typePrice}>{pricing.price.toLocaleString()} {priceText}</div>
                </button>
              );
            })}
          </div>

          {selectedPricing && selectedPricing.format === selectedFormat && (() => {
            const details = calculatePricingDetails(course, selectedPricing);
            const isSimplePricing = details.billingUnit === 'course' && details.discount === 0;
            
            return (
              <div className={styles.selectedPricingDetails}>
                <div className={styles.iosCard}>
                  <div className={styles.iosRow}>
                    <span className={styles.iosLabel}>{details.billingUnit === 'course' ? 'Стоимость курса' : 'Оплата в месяц'}</span>
                    <span className={styles.iosValueMain}>{selectedPricing.price.toLocaleString()} ₽</span>
                  </div>
                  
                  {!isSimplePricing && (
                    <>
                      <div className={styles.iosDivider}></div>
                      
                      <div className={styles.iosRow}>
                        <div className={styles.iosRowLeft}>
                          <span className={styles.iosLabel}>Общая сумма</span>
                          <span className={styles.iosSubtext}>
                            {details.billingUnit === 'course' ? 'за курс' : `за ${formatPaymentPeriod(details.paymentMonths)}`}
                          </span>
                        </div>
                        <span className={styles.iosValueSmall}>{details.total.toLocaleString()} ₽</span>
                      </div>

                      <div className={styles.iosDivider}></div>

                      <div className={styles.iosRowHighlight}>
                        <div className={styles.iosRowLeft}>
                          <span className={styles.iosLabelBold}>При полной оплате</span>
                          <span className={styles.iosSubtextGreen}>
                            экономия {details.discount.toLocaleString()} ₽
                          </span>
                        </div>
                        <span className={styles.iosValueGreen}>{details.totalUpfront.toLocaleString()} ₽</span>
                      </div>

                      {/* Временно скрыта информация об оплате за полный курс */}
                    </>
                  )}
                </div>
                
                <button 
                  className={styles.enrollButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePricingSelect(selectedPricing);
                  }}
                >
                  Записаться на курс
                </button>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};
